import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, Card, CardContent, Chip, TextField, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Avatar } from '@mui/material';
import { scopeService, proposalService, projectService } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import FileUpload from '../components/FileUpload';
import Layout from '../components/Layout';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

const TRADES = ['Plumbing', 'Electrical', 'Framing', 'Roofing', 'HVAC', 'Flooring', 'Painting', 'Concrete', 'Landscaping', 'General'];

export default function Scopes() {
  const { user } = useAuth();
  const [scopes, setScopes] = useState([]);
  const [search, setSearch] = useState('');
  const [pipelineFilter, setPipelineFilter] = useState('open');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', scope: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [open, setOpen] = useState(false);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [inviteDialog, setInviteDialog] = useState({ open: false, scope: null });
  const [subcontractors, setSubcontractors] = useState([]);
  const [selectedSubs, setSelectedSubs] = useState([]);
  const [inviteAlert, setInviteAlert] = useState('');
  const [selectedScope, setSelectedScope] = useState(null);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_id: "", title: "", description: "", trade_category: "", budget_estimate: "", deadline: "" });
  const [scopeFiles, setScopeFiles] = useState([]);
  const [projectDocs, setProjectDocs] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);

  useEffect(() => {
    if (form.project_id) {
      api.get(`/documents?project_id=${form.project_id}`).then(r => setProjectDocs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      setSelectedDocs([]);
    }
  }, [form.project_id]);
  const [proposalForm, setProposalForm] = useState({ title: "", description: "", proposed_amount: "", estimated_start_date: "", estimated_end_date: "", notes: "", attachments: [] });

  useEffect(() => {
    scopeService.getOpen().then(res => setScopes(res.data)).catch(() => {});
    if (user?.role === 'homeowner') {
      projectService.getAll().then(res => setProjects(res.data)).catch(() => {});
    }
  }, [user]);

  const handleCreateScope = async () => {
    try {
      const uploadedAttachments = scopeFiles.map(f => ({ url: f.url || f.gcsUrl, name: f.name || f.originalName, size: f.size }));
      const projectDocAttachments = selectedDocs.map(d => ({ url: d.file_url, name: d.name, size: d.file_size }));
      const attachments = [...uploadedAttachments, ...projectDocAttachments];
      const res = await scopeService.create({ ...form, attachments });
      setScopeFiles([]);
      setScopes([res.data, ...scopes]);
      setOpen(false);
    } catch (err) { alert('Failed to create scope'); }
  };

  const fetchSubcontractors = async () => {
    try {
      const res = await api.get('/scopes/subcontractors/list');
      setSubcontractors(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
  };

  const handleInvite = async () => {
    if (!selectedSubs.length) { setInviteAlert('Please select at least one subcontractor'); return; }
    try {
      const res = await api.post(`/scopes/${inviteDialog.scope.id}/invite`, { subcontractor_ids: selectedSubs });
      setInviteAlert(res.data.message);
      setTimeout(() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }, 1500);
    } catch (err) { setInviteAlert('Failed to send invitations'); }
  };

  const handleWithdraw = async (scope) => {
    setActionLoading(true);
    try {
      await api.put('/scopes/' + scope.id + '/withdraw');
      setScopes(prev => prev.map(sc => sc.id === scope.id ? { ...sc, status: 'withdrawn' } : sc));
      setSnack({ type: 'success', msg: 'Scope withdrawn. All invited subs have been notified.' });
    } catch(e) { setSnack({ type: 'error', msg: e.response?.data?.error || 'Failed to withdraw scope' }); }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', scope: null });
  };

  const handleDelete = async (scope) => {
    setActionLoading(true);
    try {
      await api.delete('/scopes/' + scope.id);
      setScopes(prev => prev.filter(sc => sc.id !== scope.id));
      setSnack({ type: 'success', msg: 'Scope deleted successfully.' });
    } catch(e) { setSnack({ type: 'error', msg: e.response?.data?.error || 'Failed to delete scope' }); }
    setActionLoading(false);
    setConfirmDialog({ open: false, type: '', scope: null });
  };

  const handleSubmitProposal = async () => {
    if (!proposalForm.attachments || proposalForm.attachments.length === 0) {
      alert('Please attach at least one proposal document.');
      return;
    }
    try {
      await proposalService.create({ ...proposalForm, attachments: proposalForm.attachments, scope_id: selectedScope.id });
      setProposalOpen(false);
      alert('Proposal submitted successfully!');
    } catch (err) { alert('Failed to submit proposal'); }
  };

  const pipelineScopes = {
    open: scopes.filter(sc => sc.status === 'open' && !sc.withdrawn_at),
    bidding: scopes.filter(sc => sc.status === 'open' && sc.proposal_count > 0),
    awarded: scopes.filter(sc => sc.status === 'awarded'),
    withdrawn: scopes.filter(sc => sc.status === 'withdrawn'),
  };
  const filtered = (pipelineFilter === 'all' ? scopes : pipelineScopes[pipelineFilter] || scopes)
    .filter(sc => sc.title.toLowerCase().includes(search.toLowerCase()) || sc.trade_category?.toLowerCase().includes(search.toLowerCase()));

  const tradeColors = { Plumbing: '#1565c0', Electrical: '#f57f17', Framing: '#4e342e', Roofing: '#37474f', HVAC: '#1b5e20', Flooring: '#880e4f', Painting: '#4a148c', Concrete: '#546e7a' };

  return (
    <Layout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
            {user?.role === 'homeowner' ? 'Scopes of Work' : 'Available Scopes'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>{filtered.length} open scopes available</Typography>
        </Box>
        {user?.role === 'homeowner' && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Post Scope</Button>
        )}
      </Box>

      <TextField fullWidth placeholder="Search by title or trade..." value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment> }} />

      {/* Snackbar */}
      {snack && (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 2,
          bgcolor: snack.type === 'success' ? '#e8f5e9' : '#ffebee',
          color: snack.type === 'success' ? '#2e7d32' : '#c62828',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
          {snack.msg}
          <Box onClick={() => setSnack(null)} sx={{ cursor: 'pointer', ml: 2, fontWeight: 700 }}>✕</Box>
        </Box>
      )}

      {/* Pipeline bar - only for owner/GC */}
      {(user?.role === 'homeowner' || user?.role === 'general_contractor') && (
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
          {[
            { key: 'open', label: 'Open', color: '#1a237e', bg: '#e8eaf6' },
            { key: 'bidding', label: 'Bidding', color: '#e65100', bg: '#fff3e0' },
            { key: 'awarded', label: 'Awarded', color: '#2e7d32', bg: '#e8f5e9' },
            { key: 'withdrawn', label: 'Withdrawn', color: '#757575', bg: '#f5f5f5' },
          ].map(p => (
            <Box key={p.key} onClick={() => setPipelineFilter(p.key)}
              sx={{ cursor: 'pointer', px: 2, py: 0.75, borderRadius: 2, fontSize: 13, fontWeight: 700,
                bgcolor: pipelineFilter === p.key ? p.color : p.bg,
                color: pipelineFilter === p.key ? 'white' : p.color,
                display: 'flex', alignItems: 'center', gap: 0.75,
                border: '1px solid ' + (pipelineFilter === p.key ? p.color : 'transparent'),
                transition: 'all 0.15s' }}>
              {p.label}
              <Box sx={{ bgcolor: pipelineFilter === p.key ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)',
                borderRadius: 10, px: 0.75, fontSize: 11, fontWeight: 800, minWidth: 18, textAlign: 'center' }}>
                {(pipelineScopes[p.key] || []).length}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Grid container spacing={3}>
        {filtered.map(scope => (
          <Grid item xs={12} md={6} lg={4} key={scope.id}>
            <Card elevation={0} sx={{ border: '1px solid #e8eaf6', borderRadius: 3, transition: 'all 0.2s', '&:hover': { boxShadow: '0 8px 24px rgba(26,35,126,0.12)', transform: 'translateY(-2px)' } }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ bgcolor: tradeColors[scope.trade_category] || '#1a237e', borderRadius: 2, width: 44, height: 44 }}>
                    <AssignmentIcon />
                  </Avatar>
                  <Chip label={scope.trade_category} size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 600 }} />
                  {scope.status === 'withdrawn' && <Chip label="Withdrawn" size="small" sx={{ bgcolor: '#f5f5f5', color: '#757575', fontWeight: 600 }} />}
                  {scope.status === 'awarded' && <Chip label="Awarded" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{scope.title}</Typography>
                {scope.project_name && <Typography variant="caption" sx={{ color: '#999', display: 'block', mb: 1 }}>📍 {scope.project_name}{scope.address ? ` — ${scope.address}` : ''}{(scope.city || scope.state) ? `, ${scope.city || ''}${scope.city && scope.state ? ', ' : ''}${scope.state || ''}` : ''}</Typography>}
                <Typography variant="body2" sx={{ color: '#666', mb: 2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{scope.description}</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AttachMoneyIcon sx={{ fontSize: 16, color: '#43a047' }} />
                    <Typography variant="body2" sx={{ color: '#43a047', fontWeight: 700 }}>${Number(scope.budget_estimate).toLocaleString()}</Typography>
                  </Box>
                  {scope.deadline && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CalendarTodayIcon sx={{ fontSize: 14, color: '#999' }} />
                      <Typography variant="caption" sx={{ color: '#666' }}>{new Date(scope.deadline).toLocaleDateString()}</Typography>
                    </Box>
                  )}
                </Box>
                {/* Scope documents - visible to all */}
                {(() => {
                  let atts = scope.attachments;
                  if (typeof atts === 'string') { try { atts = JSON.parse(atts); } catch(e) { atts = []; } }
                  if (!Array.isArray(atts) || atts.length === 0) return null;
                  return (
                    <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#f5f6fa', borderRadius: 2 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a237e', display: 'block', mb: 0.75 }}>
                        📎 Scope Documents ({atts.length})
                      </Typography>
                      {atts.map((att, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#1a237e', cursor: 'pointer', textDecoration: 'underline', '&:hover': { color: '#283593' } }}
                            onClick={() => window.open(att.url || att.gcsUrl, '_blank')}>
                            {att.name || att.originalName || 'Document ' + (i + 1)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
                {user?.role === 'subcontractor' && (
                  <Button fullWidth variant="contained" size="small"
                    onClick={() => {
                      setSelectedScope(scope);
                      setProposalForm({
                        title: scope.title || '',
                        description: scope.description || '',
                        proposed_amount: scope.budget_estimate || '',
                        estimated_start_date: '',
                        estimated_end_date: scope.deadline ? scope.deadline.split('T')[0] : '',
                        notes: '',
                        attachments: []
                      });
                      setProposalOpen(true);
                    }}
                    sx={{ bgcolor: '#1a237e', borderRadius: 2 }}>Submit Proposal</Button>
                )}
                {(user?.role === 'homeowner' || user?.role === 'general_contractor') && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 1 }}>
                    {scope.status === 'open' && (
                      <Button fullWidth variant="outlined" size="small"
                        onClick={() => { fetchSubcontractors(); setInviteDialog({ open: true, scope }); }}
                        sx={{ borderRadius: 2 }}>Invite Subcontractors</Button>
                    )}
                    {scope.status !== 'withdrawn' && scope.status !== 'awarded' && (
                      <Button fullWidth variant="outlined" size="small" color="warning"
                        onClick={() => setConfirmDialog({ open: true, type: 'withdraw', scope })}
                        sx={{ borderRadius: 2, borderColor: '#e65100', color: '#e65100', '&:hover': { bgcolor: '#fff3e0' } }}>
                        Withdraw Scope
                      </Button>
                    )}
                    {scope.status !== 'awarded' && (
                      <Button fullWidth variant="outlined" size="small" color="error"
                        onClick={() => setConfirmDialog({ open: true, type: 'delete', scope })}
                        sx={{ borderRadius: 2 }}>
                        Delete Scope
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Post New Scope</DialogTitle>
        <DialogContent>
          <TextField fullWidth select label="Project" value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} margin="normal" required>
            {projects.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} margin="normal" required />
          <TextField fullWidth label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} margin="normal" multiline rows={3} required />
          <TextField fullWidth select label="Trade Category" value={form.trade_category} onChange={e => setForm({...form, trade_category: e.target.value})} margin="normal">
            {TRADES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          <TextField fullWidth label="Budget Estimate ($)" type="number" value={form.budget_estimate} onChange={e => setForm({...form, budget_estimate: e.target.value})} margin="normal" />
          <TextField fullWidth label="Deadline" type="date" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} />
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 1 }}>Attach Documents (optional)</Typography>
            {projectDocs.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Select from project documents:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {projectDocs.map(doc => {
                    const selected = selectedDocs.find(d => d.id === doc.id);
                    return (
                      <Chip key={doc.id} label={doc.name} size="small"
                        onClick={() => setSelectedDocs(prev => selected ? prev.filter(d => d.id !== doc.id) : [...prev, doc])}
                        variant={selected ? 'filled' : 'outlined'}
                        sx={{ bgcolor: selected ? '#e8eaf6' : 'white', color: selected ? '#1a237e' : '#555',
                          borderColor: selected ? '#1a237e' : '#ddd', cursor: 'pointer', fontWeight: selected ? 700 : 400 }}/>
                    );
                  })}
                </Box>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Or upload new files:</Typography>
            <FileUpload onFilesChange={setScopeFiles} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.dwg" />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateScope} sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Post Scope</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={proposalOpen} onClose={() => setProposalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e' }}>Submit Proposal — {selectedScope?.title}</DialogTitle>
        <DialogContent>
          {selectedScope && (
            <Box sx={{ bgcolor: '#f5f7fb', border: '1px solid #e0e4f0', borderRadius: 2, p: 2, mb: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 0.5 }}>
                {selectedScope.project_name || 'Project'}
              </Typography>
              {(selectedScope.address || selectedScope.city || selectedScope.state) && (
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                  📍 {[selectedScope.address, selectedScope.city, selectedScope.state].filter(Boolean).join(', ')}
                </Typography>
              )}
              {selectedScope.trade_category && (
                <Chip label={selectedScope.trade_category} size="small" sx={{ bgcolor: '#e8eaf6', color: '#1a237e', fontWeight: 600, mb: 1 }} />
              )}
              {selectedScope.description && (
                <Typography variant="body2" sx={{ color: '#444', mb: 1, whiteSpace: 'pre-wrap' }}>
                  {selectedScope.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 1 }}>
                {selectedScope.budget_estimate != null && selectedScope.budget_estimate !== '' && (
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Budget estimate: <strong style={{ color: '#43a047' }}>${Number(selectedScope.budget_estimate).toLocaleString()}</strong>
                  </Typography>
                )}
                {selectedScope.deadline && (
                  <Typography variant="caption" sx={{ color: '#666' }}>
                    Deadline: <strong>{new Date(selectedScope.deadline).toLocaleDateString()}</strong>
                  </Typography>
                )}
              </Box>
              {Array.isArray(selectedScope.attachments) && selectedScope.attachments.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>Attachments:</Typography>
                  {selectedScope.attachments.map((a, i) => (
                    <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
                       style={{ display: 'block', fontSize: 12, color: '#1a237e', marginBottom: 2 }}>
                      📎 {a.name || `Attachment ${i + 1}`}
                    </a>
                  ))}
                </Box>
              )}
            </Box>
          )}
          <TextField fullWidth label="Proposal Title" value={proposalForm.title} onChange={e => setProposalForm({...proposalForm, title: e.target.value})} margin="normal" required />
          <TextField fullWidth label="Description" value={proposalForm.description} onChange={e => setProposalForm({...proposalForm, description: e.target.value})} margin="normal" multiline rows={3} />
          <TextField fullWidth label="Proposed Amount ($)" type="number" value={proposalForm.proposed_amount} onChange={e => setProposalForm({...proposalForm, proposed_amount: e.target.value})} margin="normal" required />
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Start Date" type="date" value={proposalForm.estimated_start_date} onChange={e => setProposalForm({...proposalForm, estimated_start_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="End Date" type="date" value={proposalForm.estimated_end_date} onChange={e => setProposalForm({...proposalForm, estimated_end_date: e.target.value})} margin="normal" InputLabelProps={{ shrink: true }} /></Grid>
          </Grid>
          <Box sx={{ mt: 2, mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#666', fontWeight: 600 }}>
              Attach proposal documents *
            </Typography>
          </Box>
          <FileUpload onFilesChange={(files) => setProposalForm({...proposalForm, attachments: files})} />
          <TextField fullWidth label="Notes" value={proposalForm.notes} onChange={e => setProposalForm({...proposalForm, notes: e.target.value})} margin="normal" multiline rows={2} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setProposalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitProposal}
            disabled={!proposalForm.proposed_amount || !proposalForm.attachments || proposalForm.attachments.length === 0}
            sx={{ bgcolor: '#1a237e', borderRadius: 2, px: 3 }}>Submit Proposal</Button>
        </DialogActions>
      </Dialog>
      {/* Invite Subcontractors Dialog */}
      <Dialog open={inviteDialog.open} onClose={() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Invite Subcontractors — {inviteDialog.scope?.title}</DialogTitle>
        <DialogContent>
          {inviteAlert && <Box sx={{ mb: 2, p: 1.5, bgcolor: inviteAlert.includes('Failed') ? '#ffebee' : '#e8f5e9', borderRadius: 2 }}><Typography variant="body2">{inviteAlert}</Typography></Box>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select subcontractors to invite. They will see this scope in their available scopes list.</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 300, overflowY: 'auto' }}>
            {subcontractors.map(sub => (
              <Box key={sub.id} onClick={() => setSelectedSubs(prev => prev.includes(sub.id) ? prev.filter(id => id !== sub.id) : [...prev, sub.id])}
                sx={{ p: 1.5, border: '1px solid', borderColor: selectedSubs.includes(sub.id) ? '#1a237e' : '#e0e0e0',
                  borderRadius: 2, cursor: 'pointer', bgcolor: selectedSubs.includes(sub.id) ? '#e8eaf6' : 'white',
                  display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: '#1a237e', fontSize: 14 }}>{sub.first_name?.[0]}</Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{sub.first_name} {sub.last_name}</Typography>
                  <Typography variant="caption" color="text.secondary">{sub.email}</Typography>
                </Box>
                {selectedSubs.includes(sub.id) && <Typography sx={{ ml: 'auto', color: '#1a237e', fontWeight: 700 }}>✓</Typography>}
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setInviteDialog({ open: false, scope: null }); setSelectedSubs([]); setInviteAlert(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleInvite} disabled={!selectedSubs.length}
            sx={{ bgcolor: '#1a237e' }}>
            Invite {selectedSubs.length > 0 ? `(${selectedSubs.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: '', scope: null })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: confirmDialog.type === 'delete' ? '#c62828' : '#e65100' }}>
          {confirmDialog.type === 'delete' ? 'Delete Scope?' : 'Withdraw Scope?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {confirmDialog.type === 'delete'
              ? 'This will permanently delete the scope. This cannot be undone.'
              : 'This will withdraw the scope and notify all invited subcontractors. They will no longer be able to submit proposals.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', scope: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" disabled={actionLoading}
            onClick={() => confirmDialog.type === 'delete' ? handleDelete(confirmDialog.scope) : handleWithdraw(confirmDialog.scope)}
            sx={{ bgcolor: confirmDialog.type === 'delete' ? '#c62828' : '#e65100', borderRadius: 2,
              '&:hover': { bgcolor: confirmDialog.type === 'delete' ? '#b71c1c' : '#d84315' } }}>
            {actionLoading ? 'Processing...' : confirmDialog.type === 'delete' ? 'Delete' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
